import { CouponCodeBackendModel, CouponCodeModel } from './coupon.model';
import {
  SeminarConditionModel,
  SeminarDateSeriesBackendModel,
  SeminarDateSeriesModel,
} from './seminar.model';
import { UserBackendListModel, UserModel } from './user.model';

export type ContractRevocationRequestModel = {
  firstName: string;
  lastName: string;
  bookingNumber: string;
  email: string;
};

export type ContractRevocationParticipantBackendModel = {
  first_name: string;
  last_name: string;
  condition: string;
  condition_price: number;
};

export type ContractRevocationSeminarDateBackendModel = {
  start_date: string;
  end_date: string;
};

export type ContractRevocationPreviewBackendModel = {
  id: number;
  booking_nr: string;
  seminar_title: string;
  seminar_series_name: string;
  seminar_start_date: string;
  seminar_dates: ContractRevocationSeminarDateBackendModel[];
  attendees: ContractRevocationParticipantBackendModel[];
  coupons: CouponCodeBackendModel[];
  status: string;
  total_price: number;
  discounted_total_price: number;
  payment_status: string;
  payment_status_label: string;
  is_paid: boolean;
  cancel_option: string;
  cancel_option_label: string;
  estimated_cancel_fee: number;
  cancellation_fee: number;
  settlement_type: 'payment' | 'refund' | 'none';
  settlement_amount: number;
  settlement_label: string;
  amount_due: number;
  within_revocation_period: boolean;
};

export type ContractRevocationParticipantModel = {
  firstName: string;
  lastName: string;
  condition: string;
  conditionPrice: number;
};

export type ContractRevocationSeminarDateModel = {
  startDate: Date;
  endDate: Date;
};

export type ContractRevocationPreviewModel = {
  bookingId: number;
  bookingNr: string;
  seminarTitle: string;
  seminarSeriesName: string;
  seminarStartDate: Date;
  seminarDates: ContractRevocationSeminarDateModel[];
  participants: ContractRevocationParticipantModel[];
  coupons: CouponCodeModel[];
  status: string;
  totalPrice: number;
  discountedPrice: number;
  paymentStatus: string;
  paymentStatusLabel: string;
  isPaid: boolean;
  cancelOption: string;
  cancelOptionLabel: string;
  estimatedCancelFee: number;
  cancellationFee: number;
  settlementType: 'payment' | 'refund' | 'none';
  settlementAmount: number;
  settlementLabel: string;
  amountDue: number;
  withinRevocationPeriod: boolean;
};

export type BookingCreateUpdateModel = {
  id?: number;
  id_seminar_date_series: number;
  participants: CourseParticipantCreateUpdateModel[];
  invoice_address: {
    facility?: string;
    first_name: string;
    last_name: string;
    email: string;
    phone_number?: string;
    street?: string;
    street_nr?: string;
    zip?: string;
    city?: string;
    country?: string;
  };
  id_coupons: number[];
  total_price: number;
  discounted_price: number;
  payment_method: string;
};

export type CourseParticipantCreateUpdateModel = {
  id?: number;
  salutation?: string;
  job_title?: string;
  first_name: string;
  last_name: string;
  email: string;
  phone_number?: string;
  street?: string;
  street_nr?: string;
  zip?: string;
  city?: string;
  country?: string;
  birthdate: string;
  questions?: string;
  condition: string;
  condition_price: number;
};

export type CourseParticipantBackendModel = {
  id: number;
  salutation: string;
  job_title: string;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  street: string;
  street_nr: string;
  zip: string;
  city: string;
  country: string;
  birthdate: string;
  questions: string;
  condition: string;
  condition_price: number;
};

export type CourseParticipantModel = {
  id: number;
  salutation?: string;
  jobTitle?: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  street?: string;
  streetNr?: string;
  zip?: string;
  city?: string;
  country?: string;
  birthdate: Date;
  questions?: string;
  condition: string;
  conditionPrice: number;
};

export type BookingBackendListModel = {
  id: number;
  id_seminar_date_series: number;
  booking_nr: string;
  id_user: number;
  user: UserBackendListModel;
  status: string;
  total_price: number;
  discounted_price: number;
  attendee_count: number;
  seminar_name: string;
  seminar_nr?: string;
  created_at: string;
  updated_at?: string;
};

export type BookingBackendModel = {
  id: number;
  seminar: {
    id: number;
    title: string;
    conditions: SeminarConditionModel[];
    category: {
      id: number;
      name: string;
      color50: string;
      color0: string;
      color70: string;
      color80: string;
      color90: string;
    };
  };
  seminar_date_series: SeminarDateSeriesBackendModel;
  user: UserBackendListModel;
  booking_nr: string;
  id_user: number;
  status: string;
  total_price: number;
  discounted_total_price: number;
  attendees: CourseParticipantBackendModel[];
  coupons?: CouponCodeBackendModel[];
  invoice?: {
    id: number;
    invoice_nr: string;
    facility: string;
    first_name: string;
    last_name: string;
    email: string;
    phone_number?: string;
    street: string;
    street_nr: string;
    zip: string;
    city: string;
    country: string;
    payment_method: string;
  };
  created_at: string;
  updated_at?: string;
};

export type BookingListModel = {
  id: number;
  bookingNr: string;
  user: UserModel;
  status: string;
  totalPrice: number;
  discountedPrice: number;
  attendeeCount: number;
  seminarName: string;
  seminarNr?: string;
  createdAt: Date;
  updatedAt?: Date;
};

export type BookingModel = {
  id: number;
  bookingNr: string;

  seminar: {
    id: number;
    title: string;
    conditions: SeminarConditionModel[];
    category: {
      id: number;
      name: string;
      color50: string;
      color0: string;
      color70: string;
      color80: string;
      color90: string;
    };
  };
  seminarDateSeries: SeminarDateSeriesModel;
  user: UserModel;
  status: string;
  totalPrice: number;
  discountedPrice: number;
  participants: CourseParticipantModel[];
  coupons?: CouponCodeModel[];
  invoice?: {
    id: number;
    facility?: string;
    invoiceNr?: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber?: string;
    street: string;
    streetNr: string;
    zip: string;
    city: string;
    country: string;
    paymentMethod: string;
  };
  createdAt: Date;
  updatedAt?: Date;
};

export enum BookingStatusEnum {
  CONFIRMED = 'confirmed',
  WAITLIST = 'waitlist',
  PENDING = 'pending',
  LATE = 'late',
}
